import React, { useState } from 'react';
import { useAppDispatch, addItemToCart, updateQuantity, RootState, usePostCartItemMutation, useUpdateCartItemMutation, useDeleteCartItemMutation } from '@circuitry-ai/doc-data';
import { useSelector } from 'react-redux';
import { checkType, CtyShowToast } from '../toast/CtyToast';
import { CtyButton } from "../button/CtyButton";
import { Icon } from '@iconify/react';
import CtySpinner from '../spinner/CtySpinner';
import type { PressEvent } from '@react-types/shared';

export interface CartActionsProps {
  partNumber: string;
  partName: string;
  referenceNumber: string;
  onSuccessCallback?: () => void;
  variant?: 'default' | 'icon';
  isActive?: boolean;
}

const LoadingSpinner = () => (
  <div className="flex justify-center">
    <CtySpinner size="sm" />
  </div>
);

const QuantityControls = ({ 
  quantity, 
  onDecrement, 
  onIncrement,
  isActive
}: { 
  quantity: number; 
  onDecrement: (e: PressEvent) => void; 
  onIncrement: (e: PressEvent) => void;
  isActive?: boolean;
}) => (
  <div className="flex items-center gap-1">
    <CtyButton 
      isIconOnly 
      size="sm" 
      variant="light" 
      onPress={onDecrement}
      aria-label="Decrease quantity"
    >
      <Icon icon="tabler:minus" width="16" height="16" className={isActive ? "text-white" : "text-primary"} />
    </CtyButton>
    <span className={`${isActive ? 'text-white' : 'text-primary'} font-semibold w-[20px] flex justify-center`}>
      {quantity}
    </span>
    <CtyButton 
      isIconOnly 
      size="sm" 
      variant="light" 
      onPress={onIncrement}
      aria-label="Increase quantity"
    >
      <Icon icon="tabler:plus" width="16" height="16" className={isActive ? "text-white" : "text-primary"} />
    </CtyButton>
  </div>
);

export const useCartActions = (
  partNumber: string, 
  partName: string, 
  referenceNumber: string,
  onSuccessCallback?: () => void
) => {
  const dispatch = useAppDispatch();
  const [postCartItem] = usePostCartItemMutation();
  const [updateCartItem] = useUpdateCartItemMutation();
  const [deleteCartItem] = useDeleteCartItemMutation();
  const [loadingItem, setLoadingItem] = useState<string | null>(null);
  
  const { itemsList, cartID } = useSelector((state: RootState) => state.globalCartSliceReducer);
  const isInCart = !!itemsList?.[partNumber];
  const quantity = itemsList?.[partNumber]?.totalQuanity ?? 0;
  
  const handleAddToCart = async () => {
    setLoadingItem(referenceNumber);
    const uniqueIdentifier = `component_${referenceNumber}`;
    
    try {
      const response = await postCartItem({
        name: partName,
        code: partNumber,
        type: 'part',
        price: 0,
        quantity: 1
      }).unwrap();
      
      dispatch(addItemToCart({
        partDetails: {
          partNumber: partNumber,
          partName: partName,
          id: response.id ?? referenceNumber,
          cartID: response.cart_id,
          quantity: 1
        },
        uniqueIdentifier
      }));
      
      CtyShowToast(checkType.success, `Added ${partName} to cart`);
      onSuccessCallback?.();
    } catch (error) {
      console.error('Error adding to cart:', error);
      CtyShowToast(checkType.error, 'Failed to add item to cart');
    } finally {
      setLoadingItem(null);
    }
  };
  
  const incrementQuantity = async () => {
    setLoadingItem(referenceNumber);
    const uniqueIdentifier = `component_${referenceNumber}`;
    
    try {
      dispatch(updateQuantity({ 
        uniqueIdentifier,
        partNumber: partNumber,
        type: 'increase' 
      }));
      
      await updateCartItem({
        cart_id: cartID,
        name: partName,
        code: partNumber,
        type: 'part',
        price: 0
      });
    } catch (error) {
      console.error('Error incrementing quantity:', error);
      CtyShowToast(checkType.error, 'Failed to update quantity');
    } finally {
      setLoadingItem(null);
    }
  };
  
  const decrementQuantity = async () => {
    setLoadingItem(referenceNumber);
    const uniqueIdentifier = `component_${referenceNumber}`;
    
    try {
      dispatch(updateQuantity({
        uniqueIdentifier,
        partNumber: partNumber,
        type: 'decrease'
      }));
      
      await deleteCartItem({
        code: partNumber,
        cartID
      });
    } catch (error) {
      console.error('Error decrementing quantity:', error);
      CtyShowToast(checkType.error, 'Failed to update quantity');
    } finally {
      setLoadingItem(null);
    }
  };

  return {
    isInCart,
    quantity,
    loadingItem,
    handleAddToCart,
    incrementQuantity,
    decrementQuantity
  };
};

export const CartActions: React.FC<CartActionsProps> = ({ 
  partNumber, 
  partName, 
  referenceNumber,
  onSuccessCallback,
  variant = 'default',
  isActive = false
}) => {
  const {
    isInCart,
    quantity,
    loadingItem,
    handleAddToCart,
    incrementQuantity,
    decrementQuantity
  } = useCartActions(partNumber, partName, referenceNumber, onSuccessCallback);
  
  const handleDecrementPress = (e: PressEvent) => decrementQuantity();
  const handleIncrementPress = (e: PressEvent) => incrementQuantity();
  const handleAddPress = (e: PressEvent) => handleAddToCart();
  const handleAddToCartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleAddToCart();
  };
  
  if (loadingItem === referenceNumber) {
    return <LoadingSpinner />;
  }
  
  if (isInCart) {
    return (
      <QuantityControls
        quantity={quantity}
        onDecrement={handleDecrementPress}
        onIncrement={handleIncrementPress}
        isActive={isActive}
      />
    );
  }
  
  if (variant === 'icon') {
    return (
      <button 
        className="focus:outline-none cursor-pointer" 
        onClick={handleAddToCartClick}
        aria-label="Add to cart"
      >
        <Icon icon="mdi:cart-outline" width="18" height="18" className={isActive ? "text-white" : "text-primary"} />
      </button>
    );
  }
  
  return (
    <CtyButton
      size="sm"
      color="primary"
      onPress={handleAddPress}
    >
      Add to cart
    </CtyButton>
  );
};

// Alias for icon variant
export const CartIcon: React.FC<CartActionsProps> = (props) => (
  <CartActions {...props} variant="icon" />
); 